import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserPlus, AlertTriangle, Trash2, Loader2, CheckCircle, Beaker,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { httpClient } from '@/api/http-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BulkMode = 'jackpot' | 'rodeo';

interface BulkParent {
  id: string;
  name: string;
  rodeo_or_jackpot: BulkMode;
  start_date: string | null;
  end_date: string | null;
  is_published: boolean;
  /** Finals rodeos ignore performance preferences on generated entries. */
  is_finals?: boolean;
  event_count: number;
  test_registration_count: number;
}

interface BulkEvent {
  id: string;
  rodeo_name: string | null;
  rodeo_or_jackpot?: BulkMode | null;
  event_name: string;
  test_registration_count: number;
}

interface BulkPerformance {
  id: string;
  performance_name: string;
  performance_date: string | null;
  start_time: string | null;
  end_time: string | null;
  is_slack: boolean;
  sort_order: number;
}

interface PaymentDistribution {
  paid: number;
  pending: number;
  cash: number;
  unpaid: number;
}

interface GenerateConfig {
  event_id: string;
  total_registrations: number;
  registration_type: 'all_individual' | 'all_team' | 'mixed';
  partner_percentage: number;
  partner_mode: 'random' | 'handicap_matched' | 'none';
  header_percentage: number;
  min_age: number | null;
  max_age: number | null;
  enforce_mixed_gender: boolean;
  enforce_century_rule: boolean;
  payment_distribution: PaymentDistribution;
  use_test_prefix: boolean;
  add_execution_id: boolean;
  simulate_time_spread: boolean;
  respect_event_limits: boolean;
  generate_missing_handicaps: boolean;
  performance_preference?: string | null;
  rodeo_performance_ids?: string[];
}

interface SampleRegistration {
  user_name: string;
  position: 'Header' | 'Heeler';
  gender: string | null;
  age: number | null;
  handicap: number | null;
  partner_name: string | null;
  partner_gender: string | null;
  partner_age: number | null;
  partner_handicap: number | null;
  combined_handicap: number | null;
  is_mixed_pair: boolean;
  is_century_pair: boolean;
  payment_status: string;
  registration_type: string;
  is_valid: boolean;
  performance_preference?: string | null;
}

interface PreviewResult {
  preview_id: string;
  total_registrations: number;
  headers_count: number;
  heelers_count: number;
  partnerships_count: number;
  sample_registrations: SampleRegistration[];
}

interface ExecuteResult {
  total_created: number;
  headers: number;
  heelers: number;
  partnerships_created: number;
  execution_id: string;
}

// ---------------------------------------------------------------------------
// Range Slider (native, themed)
// ---------------------------------------------------------------------------

function RangeSlider({ value, min, max, step, onChange }: {
  value: number; min: number; max: number; step: number;
  onChange: (v: number) => void;
}) {
  return (
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-2 rounded-full appearance-none cursor-pointer bg-muted accent-primary"
    />
  );
}

// ---------------------------------------------------------------------------
// Toggle switch styled as checkbox
// ---------------------------------------------------------------------------

function ToggleRow({ label, description, checked, onChange, disabled }: {
  label: string; description?: string; checked: boolean;
  onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <label className={`flex items-center justify-between gap-4 cursor-pointer ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium">{label}</span>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <input
        type="checkbox" checked={checked} disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-border text-primary focus:ring-primary shrink-0"
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type Tab = 'generate' | 'view' | 'cleanup';

export default function BulkRegistrationPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('generate');

  // --- Cascading selection: mode -> parent -> event -> (rodeo-only) performance ---
  const [mode, setMode] = useState<BulkMode>('jackpot');
  const [selectedParentId, setSelectedParentId] = useState('');
  const [performancePreference, setPerformancePreference] = useState<string>('any');

  // --- Generate form state ---
  const [selectedEventId, setSelectedEventId] = useState('');
  const [totalRegistrations, setTotalRegistrations] = useState(50);
  const [registrationType, setRegistrationType] = useState<GenerateConfig['registration_type']>('mixed');
  const [partnerPercentage, setPartnerPercentage] = useState(50);
  const [partnerMode, setPartnerMode] = useState<GenerateConfig['partner_mode']>('random');
  const [headerHeelerSplit, setHeaderHeelerSplit] = useState(50);
  const [minAge, setMinAge] = useState<number | null>(null);
  const [maxAge, setMaxAge] = useState<number | null>(null);
  const [enforceMixedGender, setEnforceMixedGender] = useState(false);
  const [enforceCenturyRule, setEnforceCenturyRule] = useState(false);
  const [paymentDist, setPaymentDist] = useState<PaymentDistribution>({
    paid: 40, pending: 30, cash: 20, unpaid: 10,
  });
  const [useTestPrefix, setUseTestPrefix] = useState(true);
  const [addExecutionId, setAddExecutionId] = useState(true);
  const [simulateTimeSpread, setSimulateTimeSpread] = useState(false);
  const [respectEventLimits, setRespectEventLimits] = useState(false);
  const [generateMissingHandicaps, setGenerateMissingHandicaps] = useState(true);

  const [step, setStep] = useState<'config' | 'preview' | 'executing' | 'complete'>('config');
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [executeResult, setExecuteResult] = useState<ExecuteResult | null>(null);
  const [generateError, setGenerateError] = useState('');

  // --- Cleanup state ---
  const [cleanupEventId, setCleanupEventId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState('');
  const [cleanupError, setCleanupError] = useState('');

  // --- Parents query (jackpots or rodeos, depending on mode) ---
  const { data: parentsData, isLoading: parentsLoading } = useQuery({
    queryKey: ['bulk-registration-parents', mode],
    queryFn: async () => {
      const res = await httpClient.get<{ parents: BulkParent[] }>(
        `/testing/bulk-registration/parents?mode=${mode}`,
      );
      return (res as any)?.parents ?? [];
    },
  });
  const parents: BulkParent[] = parentsData ?? [];

  // --- Events query (filtered to selected parent/mode when available, falls back to all for cleanup/view) ---
  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['bulk-registration-events', mode, selectedParentId],
    queryFn: async () => {
      const qs = new URLSearchParams();
      qs.set('mode', mode);
      if (selectedParentId) qs.set('parent_id', selectedParentId);
      const res = await httpClient.get<{ events: BulkEvent[] }>(
        `/testing/bulk-registration/events?${qs.toString()}`,
      );
      return (res as any)?.events ?? [];
    },
    enabled: !!mode,
  });
  const events: BulkEvent[] = eventsData ?? [];

  // --- Global events query (unfiltered) — used by View/Cleanup tabs ---
  const { data: allEventsData, isLoading: allEventsLoading } = useQuery({
    queryKey: ['bulk-registration-events-all'],
    queryFn: async () => {
      const res = await httpClient.get<{ events: BulkEvent[] }>(
        '/testing/bulk-registration/events?limit=200',
      );
      return (res as any)?.events ?? [];
    },
  });
  const allEvents: BulkEvent[] = allEventsData ?? [];

  // --- Performances query (rodeo mode only) ---
  const { data: performancesData, isLoading: performancesLoading } = useQuery({
    queryKey: ['bulk-registration-performances', selectedParentId],
    queryFn: async () => {
      const res = await httpClient.get<{ performances: BulkPerformance[] }>(
        `/testing/bulk-registration/performances/${selectedParentId}`,
      );
      return (res as any)?.performances ?? [];
    },
    enabled: mode === 'rodeo' && !!selectedParentId,
  });
  const performances: BulkPerformance[] = performancesData ?? [];
  const selectedParentIsFinals =
    parents.find(p => p.id === selectedParentId)?.is_finals === true;

  // --- Payment distribution normalization ---
  const handlePaymentChange = (field: keyof PaymentDistribution, value: number) => {
    setPaymentDist(prev => {
      const next = { ...prev, [field]: value };
      const sum = next.paid + next.pending + next.cash + next.unpaid;
      if (sum > 0) {
        // Normalize so they add to 100
        const scale = 100 / sum;
        return {
          paid: Math.round(next.paid * scale),
          pending: Math.round(next.pending * scale),
          cash: Math.round(next.cash * scale),
          unpaid: Math.round(next.unpaid * scale),
        };
      }
      return next;
    });
  };

  // --- Build config object ---
  const buildConfig = (): GenerateConfig => ({
    event_id: selectedEventId,
    total_registrations: totalRegistrations,
    registration_type: registrationType,
    partner_percentage: partnerPercentage / 100,
    partner_mode: partnerMode,
    header_percentage: headerHeelerSplit / 100,
    min_age: minAge,
    max_age: maxAge,
    enforce_mixed_gender: enforceMixedGender,
    enforce_century_rule: enforceCenturyRule,
    payment_distribution: {
      paid: paymentDist.paid / 100, pending: paymentDist.pending / 100,
      cash: paymentDist.cash / 100, unpaid: paymentDist.unpaid / 100,
    },
    use_test_prefix: useTestPrefix,
    add_execution_id: addExecutionId,
    simulate_time_spread: simulateTimeSpread,
    respect_event_limits: respectEventLimits,
    generate_missing_handicaps: generateMissingHandicaps,
    // Rodeo-only: performance preference + available performance IDs (for 'random' spread)
    ...(mode === 'rodeo'
      ? {
          performance_preference: performancePreference,
          rodeo_performance_ids: performances.map(p => p.id),
        }
      : {}),
  });

  // --- Preview handler ---
  const handleGeneratePreview = async () => {
    if (!selectedEventId) return;
    setGenerating(true);
    setPreview(null);
    setExecuteResult(null);
    setGenerateError('');
    try {
      const result = await httpClient.post<PreviewResult>(
        '/testing/bulk-registration/preview', buildConfig(),
      );
      setPreview(result);
      setStep('preview');
    } catch (err: any) {
      setGenerateError(err?.message ?? 'Preview generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // --- Execute handler ---
  const handleExecute = async () => {
    if (!preview) return;
    setStep('executing');
    setGenerateError('');
    try {
      const result = await httpClient.post<ExecuteResult>(
        `/testing/bulk-registration/execute/${preview.preview_id}`,
      );
      setExecuteResult(result);
      setStep('complete');
      queryClient.invalidateQueries({ queryKey: ['bulk-registration-events'] });
      queryClient.invalidateQueries({ queryKey: ['bulk-registration-events-all'] });
      queryClient.invalidateQueries({ queryKey: ['bulk-registration-parents'] });
    } catch (err: any) {
      setGenerateError(err?.message ?? 'Execution failed');
      setStep('preview');
    }
  };

  const handleBackToConfig = () => {
    setStep('config');
    setPreview(null);
    setExecuteResult(null);
  };

  // --- Cleanup handler ---
  const handleCleanup = async () => {
    setConfirmOpen(false);
    if (!cleanupEventId) return;
    setCleaning(true);
    setCleanupResult('');
    setCleanupError('');
    try {
      const res = await httpClient.delete<{ registrations_removed: number }>(
        `/testing/bulk-registration/cleanup-event/${cleanupEventId}`,
      );
      const count = (res as any)?.registrations_removed ?? 0;
      setCleanupResult(`Deleted ${count} test registrations.`);
      queryClient.invalidateQueries({ queryKey: ['bulk-registration-events'] });
      queryClient.invalidateQueries({ queryKey: ['bulk-registration-events-all'] });
      queryClient.invalidateQueries({ queryKey: ['bulk-registration-parents'] });
    } catch (err: any) {
      setCleanupError(err?.message ?? 'Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  const eventLabel = (e: BulkEvent) => {
    const parent = e.rodeo_name ?? 'Unknown';
    const kind = e.rodeo_or_jackpot === 'rodeo' ? 'Rodeo' : 'Jackpot';
    return `[${kind}] ${parent} - ${e.event_name} (${e.test_registration_count} test regs)`;
  };

  const ageOptions = Array.from({ length: 111 }, (_, i) => i);

  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <UserPlus className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Bulk Entry Testing</h1>
          <p className="text-sm text-muted-foreground">Generate test entries or clean up test data</p>
        </div>
        <Badge variant="outline" className="ml-auto">Admin Only</Badge>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {([
          { id: 'generate' as Tab, label: 'Generate Test Entries', icon: Beaker },
          { id: 'view' as Tab, label: 'View Test Data', icon: UserPlus },
          { id: 'cleanup' as Tab, label: 'Cleanup Test Data', icon: Trash2 },
        ]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ================================================================= */}
      {/* TAB 1: Generate */}
      {/* ================================================================= */}
      {activeTab === 'generate' && (
        <div className="space-y-6">
          {generateError && (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <p className="text-sm text-destructive">{generateError}</p>
            </div>
          )}

          {/* ── Step: Config ── */}
          {step === 'config' && (
            <>
              <Section
                title="Event Selection"
                subtitle="Choose between a jackpot or rodeo, pick the one you want, then select an event (and a performance for rodeos)"
              >
                {/* Mode (jackpot vs rodeo) */}
                <div className="space-y-1">
                  <label className="text-sm font-medium">Mode</label>
                  <Select
                    value={mode}
                    onValueChange={(v) => {
                      const next = v as BulkMode;
                      setMode(next);
                      setSelectedParentId('');
                      setSelectedEventId('');
                      setPerformancePreference('any');
                      // handicap_matched is jackpot-only — reset to 'random'
                      // when switching into rodeo mode so we don't send an
                      // invalid value the backend would silently ignore.
                      if (next === 'rodeo' && partnerMode === 'handicap_matched') {
                        setPartnerMode('random');
                      }
                    }}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="jackpot">Jackpot</SelectItem>
                      <SelectItem value="rodeo">Rodeo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Parent (specific jackpot or rodeo) */}
                <div className="space-y-1">
                  <label className="text-sm font-medium">{mode === 'rodeo' ? 'Rodeo' : 'Jackpot'}</label>
                  {parentsLoading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Select
                      value={selectedParentId}
                      onValueChange={(v) => {
                        setSelectedParentId(v);
                        setSelectedEventId('');
                        setPerformancePreference('any');
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={`Select a ${mode}...`} />
                      </SelectTrigger>
                      <SelectContent>
                        {parents.length === 0 ? (
                          <SelectItem value="__none__" disabled>
                            No {mode}s found
                          </SelectItem>
                        ) : (
                          parents.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} ({p.event_count} event{p.event_count === 1 ? '' : 's'}
                              {p.test_registration_count > 0 ? `, ${p.test_registration_count} test entries` : ''})
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Event (filtered by parent) */}
                <div className="space-y-1">
                  <label className="text-sm font-medium">Event</label>
                  {!selectedParentId ? (
                    <p className="text-xs text-muted-foreground">Select a {mode} first to see its events.</p>
                  ) : eventsLoading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                      <SelectTrigger><SelectValue placeholder="Select an event..." /></SelectTrigger>
                      <SelectContent>
                        {events.length === 0 ? (
                          <SelectItem value="__none__" disabled>
                            No events found
                          </SelectItem>
                        ) : (
                          events.map(e => (
                            <SelectItem key={e.id} value={e.id}>
                              {e.event_name || 'Unnamed event'}
                              {e.test_registration_count > 0 ? ` (${e.test_registration_count} test entries)` : ''}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Performance selector (rodeo-only, and not on finals
                    rodeos — those seat every contestant in every
                    performance, so the backend ignores the value). */}
                {mode === 'rodeo' && selectedParentId && !selectedParentIsFinals && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Performance Preference</label>
                    {performancesLoading ? (
                      <Skeleton className="h-10 w-full" />
                    ) : (
                      <Select value={performancePreference} onValueChange={setPerformancePreference}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any (no preference)</SelectItem>
                          {performances.length > 1 && (
                            <SelectItem value="random">Random (spread across performances)</SelectItem>
                          )}
                          {performances.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.performance_name}
                              {p.performance_date ? ` — ${p.performance_date}` : ''}
                              {p.is_slack ? ' (slack)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Sets <code>first_performance_preference</code> on every generated entry.
                      {performances.length === 0 && ' This rodeo has no performance slots defined yet.'}
                    </p>
                  </div>
                )}
              </Section>

              <Section title="Basic Configuration" subtitle="Configure the number and type of entries to generate">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Total Entries: {totalRegistrations}</label>
                  <RangeSlider value={totalRegistrations} min={1} max={500} step={1} onChange={setTotalRegistrations} />
                  <div className="flex justify-between text-xs text-muted-foreground"><span>1</span><span>500</span></div>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Entry Type</label>
                  <Select value={registrationType} onValueChange={v => setRegistrationType(v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_individual">All Individual</SelectItem>
                      <SelectItem value="all_team">All Team</SelectItem>
                      <SelectItem value="mixed">Mixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </Section>

              <Section title="Partner Configuration" subtitle="Configure how partners are selected and paired">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Partner Percentage: {partnerPercentage}%</label>
                  <RangeSlider value={partnerPercentage} min={0} max={100} step={5} onChange={setPartnerPercentage} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Partner Selection Mode</label>
                  <Select value={partnerMode} onValueChange={v => setPartnerMode(v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="random">Random</SelectItem>
                      {/* Handicaps are jackpot-only; matched-handicap pairing
                          is unavailable when the bulk run targets a rodeo. */}
                      {mode !== 'rodeo' && (
                        <SelectItem value="handicap_matched">Handicap Matched</SelectItem>
                      )}
                      <SelectItem value="none">No Partners</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Header/Heeler Split: {headerHeelerSplit}% Headers</label>
                  <RangeSlider value={headerHeelerSplit} min={0} max={100} step={5} onChange={setHeaderHeelerSplit} />
                </div>
              </Section>

              <Section title="Age Filters" subtitle="Restrict user selection by age range">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Minimum Age</label>
                    <Select value={minAge === null ? 'any' : String(minAge)} onValueChange={v => setMinAge(v === 'any' ? null : parseInt(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="any">ANY</SelectItem>{ageOptions.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Maximum Age</label>
                    <Select value={maxAge === null ? 'any' : String(maxAge)} onValueChange={v => setMaxAge(v === 'any' ? null : parseInt(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="any">ANY</SelectItem>{ageOptions.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </Section>

              <Section title="Partnership Rules" subtitle="Apply special rules when creating partnerships">
                <ToggleRow label="Enforce Mixed Gender" description="Ensure partners are opposite genders (for mixed events)"
                  checked={enforceMixedGender} onChange={v => { setEnforceMixedGender(v); if (v) setEnforceCenturyRule(false); }} disabled={enforceCenturyRule} />
                <ToggleRow label="Enforce Century Rule" description="Ensure partners' combined age >= 100 (for century events)"
                  checked={enforceCenturyRule} onChange={v => { setEnforceCenturyRule(v); if (v) setEnforceMixedGender(false); }} disabled={enforceMixedGender} />
              </Section>

              <Section title="Payment Distribution" subtitle="Configure the distribution of payment statuses">
                <div className="grid grid-cols-2 gap-4">
                  {([['paid','Paid',paymentDist.paid],['pending','Pending',paymentDist.pending],['cash','Cash',paymentDist.cash],['unpaid','Unpaid',paymentDist.unpaid]] as const).map(([key,label,val]) => (
                    <div key={key} className="space-y-2">
                      <label className="text-sm font-medium">{label}: {val}%</label>
                      <RangeSlider value={val} min={0} max={100} step={5} onChange={v => handlePaymentChange(key, v)} />
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Test Settings" subtitle="Configure test-specific options">
                <ToggleRow label="Add TEST_ prefix to names" checked={useTestPrefix} onChange={setUseTestPrefix} />
                <ToggleRow label="Track with execution ID" checked={addExecutionId} onChange={setAddExecutionId} />
                <ToggleRow label="Spread entry times" checked={simulateTimeSpread} onChange={setSimulateTimeSpread} />
                <ToggleRow label="Respect event capacity limits" checked={respectEventLimits} onChange={setRespectEventLimits} />
                {/* Handicaps are jackpot-only — hide the toggle for rodeo runs;
                    backend ignores the flag for rodeo events regardless. */}
                {mode !== 'rodeo' && (
                  <ToggleRow label="Generate handicaps for users without them" checked={generateMissingHandicaps} onChange={setGenerateMissingHandicaps} />
                )}
              </Section>

              <Button size="lg" className="w-full" disabled={!selectedEventId || generating} onClick={handleGeneratePreview}>
                {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating Preview...</> : <><Beaker className="mr-2 h-4 w-4" />Generate Preview</>}
              </Button>
            </>
          )}

          {/* ── Step: Preview ── */}
          {step === 'preview' && preview && (() => {
            const regs = preview.sample_registrations;
            const totalEntries = regs.length;
            const totalUsers = preview.total_registrations;
            const males = regs.reduce((n, r) => {
              let c = 0;
              if (r.gender?.toLowerCase() === 'male') c++;
              if (r.partner_gender?.toLowerCase() === 'male') c++;
              return n + c;
            }, 0);
            const females = regs.reduce((n, r) => {
              let c = 0;
              if (r.gender?.toLowerCase() === 'female') c++;
              if (r.partner_gender?.toLowerCase() === 'female') c++;
              return n + c;
            }, 0);
            return (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
                {([
                  ['Entries', totalEntries, ''],
                  ['Users', totalUsers, ''],
                  ['Males', males, 'text-blue-500'],
                  ['Females', females, 'text-pink-500'],
                  ['Headers', preview.headers_count, 'text-blue-600'],
                  ['Heelers', preview.heelers_count, 'text-purple-500'],
                  ['Pairs', preview.partnerships_count, 'text-green-500'],
                ] as [string, number, string][]).map(([label, val, color]) => (
                  <div key={label} className="rounded-lg bg-muted/50 p-3">
                    <div className="text-xs text-muted-foreground">{label}</div>
                    <div className={`text-lg font-bold ${color}`}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Sample Registrations Table */}
              <div className="rounded-md border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-2 py-2.5 text-center text-xs font-medium text-muted-foreground uppercase w-8">#</th>
                        <th className="px-2 py-2.5 text-center text-xs font-medium text-muted-foreground uppercase w-8"></th>
                        <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Header</th>
                        <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">G</th>
                        <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Age</th>
                        <th className="px-2 py-2.5 text-right text-xs font-medium text-muted-foreground uppercase">HC</th>
                        <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Heeler</th>
                        <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">G</th>
                        <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Age</th>
                        <th className="px-2 py-2.5 text-right text-xs font-medium text-muted-foreground uppercase">HC</th>
                        <th className="px-2 py-2.5 text-right text-xs font-medium text-muted-foreground uppercase">Total</th>
                        <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Rules</th>
                        <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Payment</th>
                        <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Entry</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {preview.sample_registrations.map((reg, i) => {
                        const isPair = reg.registration_type === 'pair';
                        const isHeader = reg.position === 'Header';
                        const headerName = isPair ? reg.user_name : (isHeader ? reg.user_name : null);
                        const headerGender = isPair ? reg.gender : (isHeader ? reg.gender : null);
                        const headerAge = isPair ? reg.age : (isHeader ? reg.age : null);
                        const headerHC = isPair ? reg.handicap : (isHeader ? reg.handicap : null);
                        const heelerName = isPair ? reg.partner_name : (!isHeader ? reg.user_name : null);
                        const heelerGender = isPair ? reg.partner_gender : (!isHeader ? reg.gender : null);
                        const heelerAge = isPair ? reg.partner_age : (!isHeader ? reg.age : null);
                        const heelerHC = isPair ? reg.partner_handicap : (!isHeader ? reg.handicap : null);
                        const hcFmt = (v: number | null | undefined) => v != null ? v.toFixed(1) : '—';

                        return (
                          <tr key={i}>
                            <td className="px-2 py-2 text-center text-xs text-muted-foreground font-mono">{i + 1}</td>
                            <td className="px-2 py-2 text-center">
                              {reg.is_valid
                                ? <CheckCircle className="h-4 w-4 text-green-500" />
                                : <AlertTriangle className="h-4 w-4 text-red-500" />}
                            </td>
                            <td className="px-2 py-2 whitespace-nowrap">
                              {headerName ? (
                                <span className="flex items-center gap-1.5">
                                  <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-500/15 text-blue-600 text-[10px] font-bold shrink-0" title="Header">H</span>
                                  <span className="font-medium">{headerName}</span>
                                </span>
                              ) : <span className="text-muted-foreground text-xs">—</span>}
                            </td>
                            <td className="px-2 py-2 text-xs capitalize">{headerGender ? headerGender[0].toUpperCase() : '—'}</td>
                            <td className="px-2 py-2 text-xs font-mono">{headerAge ?? '—'}</td>
                            <td className="px-2 py-2 text-xs font-mono text-right">{hcFmt(headerHC)}</td>
                            <td className="px-2 py-2 whitespace-nowrap">
                              {heelerName ? (
                                <span className="flex items-center gap-1.5">
                                  <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-purple-500/15 text-purple-600 text-[10px] font-bold shrink-0" title="Heeler">He</span>
                                  <span>{heelerName}</span>
                                </span>
                              ) : <span className="text-muted-foreground text-xs">—</span>}
                            </td>
                            <td className="px-2 py-2 text-xs capitalize">{heelerGender ? heelerGender[0].toUpperCase() : '—'}</td>
                            <td className="px-2 py-2 text-xs font-mono">{heelerAge ?? '—'}</td>
                            <td className="px-2 py-2 text-xs font-mono text-right">{hcFmt(heelerHC)}</td>
                            <td className="px-2 py-2 text-xs font-mono text-right font-semibold">
                              {reg.combined_handicap != null ? reg.combined_handicap.toFixed(1) : '—'}
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex gap-1">
                                {reg.is_mixed_pair && (
                                  <Badge className="text-[10px] px-1.5 py-0 bg-pink-500/15 text-pink-600 border-pink-500/30 hover:bg-pink-500/15">Mixed</Badge>
                                )}
                                {reg.is_century_pair && (
                                  <Badge className="text-[10px] px-1.5 py-0 bg-amber-500/15 text-amber-600 border-amber-500/30 hover:bg-amber-500/15">100+</Badge>
                                )}
                                {!isPair && <span className="text-muted-foreground text-xs">—</span>}
                              </div>
                            </td>
                            <td className="px-2 py-2">
                              {(() => {
                                const ps = reg.payment_status;
                                const colors: Record<string, string> = {
                                  paid: 'bg-green-500/15 text-green-600 border-green-500/30',
                                  pending: 'bg-yellow-500/15 text-yellow-600 border-yellow-500/30',
                                  cash: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
                                  unpaid: 'bg-red-500/15 text-red-600 border-red-500/30',
                                };
                                return <Badge className={`text-xs ${colors[ps] ?? ''} hover:opacity-100`}>{ps}</Badge>;
                              })()}
                            </td>
                            <td className="px-2 py-2">
                              {isPair ? (
                                <Badge className="text-xs bg-blue-500/15 text-blue-600 border-blue-500/30 hover:bg-blue-500/15">Pair</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">Individual</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="bg-muted/50 px-4 py-2 text-center text-xs text-muted-foreground">
                  Showing all {preview.sample_registrations.length} registrations
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex justify-between">
                <Button variant="outline" onClick={executeResult ? () => setStep('complete') : handleBackToConfig}>
                  {executeResult ? 'Back to Results' : 'Back to Configuration'}
                </Button>
                {!executeResult && (
                  <Button onClick={handleExecute}>
                    Execute Registration
                  </Button>
                )}
              </div>
            </div>
          ); })()}

          {/* ── Step: Executing ── */}
          {step === 'executing' && (
            <div className="flex flex-col items-center gap-4 py-12">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-lg font-medium">Executing bulk entry...</p>
              <p className="text-sm text-muted-foreground">Creating {preview?.total_registrations ?? 0} entries</p>
            </div>
          )}

          {/* ── Step: Complete ── */}
          {step === 'complete' && executeResult && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-green-500/20 bg-green-500/5 p-4">
                <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                <div className="text-sm space-y-1">
                  <p className="font-medium text-green-600">Entry Complete</p>
                  <p className="text-muted-foreground">
                    Successfully created {preview?.sample_registrations.length ?? 0} entries ({executeResult.total_created} users)
                  </p>
                </div>
              </div>

              <div className="rounded-lg bg-muted/50 p-4">
                <h4 className="font-medium mb-2">Execution Summary</h4>
                <dl className="grid grid-cols-3 md:grid-cols-5 gap-4 text-sm">
                  <div><dt className="text-muted-foreground">Entries</dt><dd className="font-medium">{preview?.sample_registrations.length ?? 0}</dd></div>
                  <div><dt className="text-muted-foreground">Users</dt><dd className="font-medium">{executeResult.total_created}</dd></div>
                  <div><dt className="text-muted-foreground">Headers</dt><dd className="font-medium">{executeResult.headers}</dd></div>
                  <div><dt className="text-muted-foreground">Heelers</dt><dd className="font-medium">{executeResult.heelers}</dd></div>
                  <div><dt className="text-muted-foreground">Pairs</dt><dd className="font-medium">{executeResult.partnerships_created}</dd></div>
                </dl>
              </div>

              <div className="flex justify-between">
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setActiveTab('cleanup'); }}>
                    <Trash2 className="mr-2 h-4 w-4" /> Cleanup Test Data
                  </Button>
                  {preview && (
                    <Button variant="outline" onClick={() => setStep('preview')}>
                      View Entries
                    </Button>
                  )}
                </div>
                <Button onClick={handleBackToConfig}>Generate More</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ================================================================= */}
      {/* TAB: View Test Data */}
      {/* ================================================================= */}
      {activeTab === 'view' && (
        <ViewTestDataTab events={allEvents} eventsLoading={allEventsLoading} eventLabel={eventLabel} />
      )}

      {/* ================================================================= */}
      {/* TAB: Cleanup */}
      {/* ================================================================= */}
      {activeTab === 'cleanup' && (
        <div className="space-y-6">
          <div className="flex items-start gap-3 rounded-lg border border-yellow-500/20 bg-yellow-500/5 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-yellow-600" />
            <div>
              <p className="text-sm font-medium text-yellow-600">Warning: Permanent Deletion</p>
              <p className="mt-1 text-sm text-muted-foreground">
                This will permanently delete ALL test entries for the selected event.
                Only test entries (created by the bulk entry tool) will be affected.
                Real user entries will NOT be deleted.
              </p>
            </div>
          </div>

          <Section title="Select Event to Clean" subtitle="Choose the event whose test data you want to remove (jackpots and rodeos)">
            {allEventsLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <div className="space-y-1">
                <label className="text-sm font-medium">Event</label>
                <Select value={cleanupEventId} onValueChange={setCleanupEventId}>
                  <SelectTrigger><SelectValue placeholder="Select an event..." /></SelectTrigger>
                  <SelectContent>
                    {allEvents.filter(e => e.test_registration_count > 0).map(e => (
                      <SelectItem key={e.id} value={e.id}>{eventLabel(e)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {cleanupEventId && (
              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-3">
                  Ready to delete test entries for the selected event.
                </p>
                <Button variant="destructive" disabled={cleaning} onClick={() => setConfirmOpen(true)}>
                  {cleaning
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</>
                    : <><Trash2 className="mr-2 h-4 w-4" />Delete All Test Entries</>}
                </Button>
              </div>
            )}
          </Section>

          {cleanupResult && (
            <div className="flex items-start gap-3 rounded-lg border border-green-500/20 bg-green-500/5 p-4">
              <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
              <p className="text-sm font-medium text-green-600">{cleanupResult}</p>
            </div>
          )}
          {cleanupError && (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <p className="text-sm text-destructive">{cleanupError}</p>
            </div>
          )}
        </div>
      )}

      {/* Confirm cleanup dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Test Entries</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete all test entries for the selected event?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleCleanup}>Delete Entries</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// View Test Data Tab
// ---------------------------------------------------------------------------

function ViewTestDataTab({ events, eventsLoading, eventLabel }: {
  events: BulkEvent[]; eventsLoading: boolean; eventLabel: (e: BulkEvent) => string;
}) {
  const [selectedEventId, setSelectedEventId] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['bulk-test-registrations', selectedEventId],
    queryFn: () => httpClient.get<any>(`/testing/bulk-registration/test-registrations/${selectedEventId}`),
    enabled: !!selectedEventId,
  });

  const entries: any[] = data?.entries ?? [];
  const totalEntries = data?.total_entries ?? 0;
  const totalUsers = data?.total_users ?? 0;
  const pairCount = entries.filter(e => e.type === 'pair').length;
  const males = entries.reduce((n: number, e: any) => {
    let c = 0;
    if (e.header_gender?.toLowerCase() === 'male') c++;
    if (e.heeler_gender?.toLowerCase() === 'male') c++;
    return n + c;
  }, 0);
  const females = entries.reduce((n: number, e: any) => {
    let c = 0;
    if (e.header_gender?.toLowerCase() === 'female') c++;
    if (e.heeler_gender?.toLowerCase() === 'female') c++;
    return n + c;
  }, 0);
  const hcFmt = (v: number | null | undefined) => v != null ? Number(v).toFixed(1) : '—';

  return (
    <div className="space-y-6">
      <Section title="Select Event" subtitle="View all test entries for an event">
        {eventsLoading ? <Skeleton className="h-10 w-full" /> : (
          <div className="space-y-1">
            <label className="text-sm font-medium">Event</label>
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger><SelectValue placeholder="Select an event..." /></SelectTrigger>
              <SelectContent>
                {events.filter(e => e.test_registration_count > 0).map(e => (
                  <SelectItem key={e.id} value={e.id}>{eventLabel(e)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </Section>

      {selectedEventId && isLoading && (
        <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      )}

      {selectedEventId && !isLoading && entries.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
            {([
              ['Entries', totalEntries, ''], ['Users', totalUsers, ''],
              ['Males', males, 'text-blue-500'], ['Females', females, 'text-pink-500'],
              ['Headers', entries.filter((e: any) => e.header_name).length, 'text-blue-600'],
              ['Heelers', entries.filter((e: any) => e.heeler_name).length, 'text-purple-500'],
              ['Pairs', pairCount, 'text-green-500'],
            ] as [string, number, string][]).map(([label, val, color]) => (
              <div key={label} className="rounded-lg bg-muted/50 p-3">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className={`text-lg font-bold ${color}`}>{val}</div>
              </div>
            ))}
          </div>

          <div className="rounded-md border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-2 py-2.5 text-center text-xs font-medium text-muted-foreground uppercase w-8">#</th>
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Header</th>
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">G</th>
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Age</th>
                    <th className="px-2 py-2.5 text-right text-xs font-medium text-muted-foreground uppercase">HC</th>
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Heeler</th>
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">G</th>
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Age</th>
                    <th className="px-2 py-2.5 text-right text-xs font-medium text-muted-foreground uppercase">HC</th>
                    <th className="px-2 py-2.5 text-right text-xs font-medium text-muted-foreground uppercase">Total</th>
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Rules</th>
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Entry</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {entries.map((e: any, i: number) => (
                    <tr key={i}>
                      <td className="px-2 py-2 text-center text-xs text-muted-foreground font-mono">{i + 1}</td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        {e.header_name ? (
                          <span className="flex items-center gap-1.5">
                            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-500/15 text-blue-600 text-[10px] font-bold shrink-0">H</span>
                            <span className="font-medium">{e.header_name}</span>
                          </span>
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </td>
                      <td className="px-2 py-2 text-xs capitalize">{e.header_gender ? e.header_gender[0].toUpperCase() : '—'}</td>
                      <td className="px-2 py-2 text-xs font-mono">{e.header_age ?? '—'}</td>
                      <td className="px-2 py-2 text-xs font-mono text-right">{hcFmt(e.header_handicap)}</td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        {e.heeler_name ? (
                          <span className="flex items-center gap-1.5">
                            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-purple-500/15 text-purple-600 text-[10px] font-bold shrink-0">He</span>
                            <span>{e.heeler_name}</span>
                          </span>
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </td>
                      <td className="px-2 py-2 text-xs capitalize">{e.heeler_gender ? e.heeler_gender[0].toUpperCase() : '—'}</td>
                      <td className="px-2 py-2 text-xs font-mono">{e.heeler_age ?? '—'}</td>
                      <td className="px-2 py-2 text-xs font-mono text-right">{hcFmt(e.heeler_handicap)}</td>
                      <td className="px-2 py-2 text-xs font-mono text-right font-semibold">{e.combined_handicap != null ? Number(e.combined_handicap).toFixed(1) : '—'}</td>
                      <td className="px-2 py-2">
                        <div className="flex gap-1">
                          {e.is_mixed && <Badge className="text-[10px] px-1.5 py-0 bg-pink-500/15 text-pink-600 border-pink-500/30 hover:bg-pink-500/15">Mixed</Badge>}
                          {e.is_century && <Badge className="text-[10px] px-1.5 py-0 bg-amber-500/15 text-amber-600 border-amber-500/30 hover:bg-amber-500/15">100+</Badge>}
                          {e.type !== 'pair' && <span className="text-muted-foreground text-xs">—</span>}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        {e.type === 'pair' ? (
                          <Badge className="text-xs bg-blue-500/15 text-blue-600 border-blue-500/30 hover:bg-blue-500/15">Pair</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Individual</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-muted/50 px-4 py-2 text-center text-xs text-muted-foreground">Showing all {entries.length} entries</div>
          </div>
        </div>
      )}

      {selectedEventId && !isLoading && entries.length === 0 && (
        <div className="rounded-lg border p-8 text-center">
          <p className="text-muted-foreground">No test entries found for this event.</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reusable section wrapper
// ---------------------------------------------------------------------------

function Section({ title, subtitle, children }: {
  title: string; subtitle: string; children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-muted/50 p-6 space-y-4">
      <div>
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}
